import { useEffect, useState } from "react";
import { Container, Spinner } from "react-bootstrap";
import {
  CartesianGrid,
  Legend, Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis, YAxis
} from 'recharts';
import { DEMAND_UNIT, LINE_GRAPH_PALETTE, SP_DARK_BLUE } from "../helper/constants";
import { getCurrentDate, getCurrentPeriod, getCurrentTime, isEmpty, periodToTime, roundTo2dp, timeToPeriod } from "../helper/utils";
import { getProtectedData } from "../services/api";
import './MarketAnalysis.css';


const MarketAnalysis = () => {
  const [data, setData] = useState({});
  const [error, setError] = useState({});
  const [forecastData, setForecastData] = useState(null);
  const [participationData, setParticipationData] = useState(null);
  const [modelInfo, setModelInfo] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [refresh, setRefresh] = useState(true);
  const [yDomains, setYDomains] = useState({
    demand: [0, 10000],
    usep: [0, 4500],
  });

  // Graph constants START
  const DEMAND = "Demand";
  const SOLAR = "Solar";
  const EMC_ACTUAL_DEMAND = "Demand";
  const EMC_FORECASTED_DEMAND = "EMC Demand Forecasts";
  const SDC_ACTUAL_DEMAND = "SDC Previous Demand Forecasts";
  const SDC_FORECASTED_DEMAND = "SDC Upcoming Demand Forecasts";
  const USEP = "USEP";
  const EMC_FORECASTED_USEP = "EMC USEP Forecasts";
  const SDC_ACTUAL_USEP = "SDC Previous USEP Forecasts";
  const SDC_FORECASTED_USEP = "SDC Upcoming USEP Forecasts";
  const PERIOD = "Period";
  const TIME = "Time";
  const SDC_USEP = "SDC USEP Forecast";
  const SOLAR_LOSS = "SDC Demand w/o Solar";
  const FORECAST_GRAPH_PARAMS = [
    [SDC_ACTUAL_DEMAND, SDC_FORECASTED_DEMAND],
    [EMC_ACTUAL_DEMAND, EMC_FORECASTED_DEMAND],
    [SDC_ACTUAL_USEP, SDC_FORECASTED_USEP],
    [USEP, EMC_FORECASTED_USEP],
  ];
  const PARTICIPATE = "participate";
  const CURTAILED_LOAD = "curtailed_load";
  // Graph constants END

  // Retrieve data from DB
  useEffect(() => {
    setLoaded(false);

    const CURRENT_DATE = getCurrentDate();
    // Weather data endpoint: "getWeatherData" (remove "get" when adding to array)
    // Weather forecast endpoint: "getWeatherForecast" (remove "get" when adding to array)
    (async () => {
      await Promise.all([
        {
          name: "Dpr",
          params: {
            date: CURRENT_DATE,
          }
        },
        {
          name: "DemandForecast",
          params: {
            current_date: CURRENT_DATE,
          }
        },
        {
          name: "USEPForecast",
          params: {
            date: CURRENT_DATE,
          }
        },
        {
          name: "DRForecast",
          params: {
            date: CURRENT_DATE,
          }
        },
        {
          name: "PeriodicCurtailedLoad",
          params: {
            date: CURRENT_DATE,
          }
        },
      ].map((endpoint) => getProtectedData(
        `/get${endpoint.name}`,
        { params: endpoint.params },
        (resData) => {
          setData((d) => ({...d, [endpoint.name]: resData}));
          setError((e) => {
            if (e.hasOwnProperty(endpoint.name)) {
              delete e[endpoint.name];
            }
            return e;
          });
        }, (error) => { setError((e) => ({...e, [endpoint.name]: error})); }
      )));
    })();

    const interval = setInterval(() => setRefresh((r) => !r), 60 * 1000); // Update data every minute
    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [refresh]);

  // Process/stage data START
  useEffect(() => {
    setLoaded(false);
    
    const currentPeriod = getCurrentPeriod();

    if (!isEmpty(data)) {
      const selectedModelInfo = getMostAccurateModelInfo();
      const model = isEmpty(selectedModelInfo) ? null : selectedModelInfo[0];
      if (!isEmpty(selectedModelInfo)) {
        setModelInfo(selectedModelInfo);
      }

      if (!isEmpty(data.Dpr) && model) {
        setForecastData(Object
          .entries(data.Dpr)
          .reduce((arr, [period, values]) => {
            const isForecast = parseInt(period, 10) > parseInt(currentPeriod, 10);
            const entry = {
              [PERIOD]: parseInt(period, 10),
              [isForecast ? EMC_FORECASTED_DEMAND : EMC_ACTUAL_DEMAND]: Math.round(getArrSum([values[DEMAND], values[SOLAR]])),
              [isForecast ? SDC_FORECASTED_DEMAND : SDC_ACTUAL_DEMAND]: getSdcDemandForecastByPeriod(period, model),
              [isForecast ? EMC_FORECASTED_USEP : USEP]: values[USEP] ? values[USEP].toFixed(2) : null,
            };
            if (!isEmpty(data.USEPForecast)) {
              if (
                data.USEPForecast.hasOwnProperty(period) &&
                data.USEPForecast[period].hasOwnProperty("Predicted_USEP")
              ) {
                entry[isForecast ? SDC_FORECASTED_USEP: SDC_ACTUAL_USEP] = data.USEPForecast[period].Predicted_USEP.toFixed(2);
              } else if (
                arr.length > 0
              ) {
                if (arr[arr.length-1][SDC_FORECASTED_USEP]) {
                  entry[isForecast ? SDC_FORECASTED_USEP: SDC_ACTUAL_USEP] = arr[arr.length-1][SDC_FORECASTED_USEP];
                } else if (arr[arr.length-1][SDC_ACTUAL_USEP]) {
                  entry[isForecast ? SDC_FORECASTED_USEP: SDC_ACTUAL_USEP] = arr[arr.length-1][SDC_ACTUAL_USEP];
                }
              }
            }
            arr.push({
              ...entry,
              ...(
                parseInt(period, 10) == parseInt(currentPeriod, 10) ?
                  {
                    [EMC_FORECASTED_DEMAND]: entry[EMC_ACTUAL_DEMAND],
                    [SDC_FORECASTED_DEMAND]: entry[SDC_ACTUAL_DEMAND],
                    [EMC_FORECASTED_USEP]: entry[USEP],
                    [SDC_FORECASTED_USEP]: entry[SDC_ACTUAL_USEP],
                  } : {}
              ),
            });
            return arr;
          }, [])
        );
      }

      if (!isEmpty(data.USEPForecast) && model) {
        setParticipationData(Object
          .entries(data.USEPForecast)
          .reduce((arr, [period, usepData]) => {
            period = parseInt(period, 10);
            if (period <= currentPeriod + 2 || period > currentPeriod + 5) { return arr; } // NOTE: filter which periods to show
            const sdcDemandForecast = getSdcDemandForecastByPeriod(period, model);
            // Set data
            arr.push({
              [PERIOD]: period,
              [TIME]: periodToTime(period) + " - " + periodToTime(period+1),
              [SDC_FORECASTED_DEMAND]: sdcDemandForecast,
              [SOLAR_LOSS]: data.Dpr && data.Dpr[period] && data.Dpr[period][SOLAR] && sdcDemandForecast ?
                `${sdcDemandForecast-Math.round(data.Dpr[period][SOLAR])} ${DEMAND_UNIT} (${getDelta(0, data.Dpr[period][SOLAR])})` :
                null,
              [SDC_USEP]: usepData.Predicted_USEP ? usepData.Predicted_USEP.toFixed(2) : null,
              [PARTICIPATE]: !isEmpty(data.USEPForecast) && data.USEPForecast[period] ?
                (
                  data.USEPForecast[period] === 1 ?
                    <span className="green text-icon">✔</span> :
                    <span>-</span>
                ) : null,
              [CURTAILED_LOAD]: (
                !isEmpty(data.PeriodicCurtailedLoad) &&
                data.PeriodicCurtailedLoad[period] &&
                data.PeriodicCurtailedLoad[period].tcl
              ) ? data.PeriodicCurtailedLoad[period].tcl.toFixed(1) : null,
            });
            return arr;
          }, [])
          .sort((a, b) => b[PERIOD] - a[PERIOD])
        );
      }
    }

    if (error.hasOwnProperty("USEPForecast")) { setParticipationData({}); }
    if (error.hasOwnProperty("Dpr")) { setForecastData({}); }

    setLoaded(true);
  }, [data, error]);

  useEffect(() => {
    if (!isEmpty(forecastData)) {
      setYDomains({
        demand: getForecastGraphYDomain(forecastData, EMC_ACTUAL_DEMAND, SDC_ACTUAL_DEMAND),
        usep: getForecastGraphYDomain(forecastData, USEP, SDC_ACTUAL_USEP),
      });
    }
  }, [forecastData]);

  const getMissingDataMessage = (dataset) => (dataset === null ?
    <Spinner animation="border" variant="primary" /> : <p className="no-data-text">No data found.</p>
  );
  // Process/stage data END

  // Data processing START
  const getArrSum = (values) => {
    try {
      return values.reduce((sum, curr) => curr ? curr + sum : sum, 0);
    } catch (error) {
      return 0;
    }
  }
  /**
   *
   * @returns {[String, Number]} name, accuracy (2 d.p.) of most accurate model
   */
  const getMostAccurateModelInfo = () => {
    if (!isEmpty(data.DemandForecast) && !isEmpty(data.Dpr)) {
      // Get all model names
      const models = Object.values(data.DemandForecast).reduce((modelNames, modelDict) => {
        Object.keys(modelDict).forEach(modelName => {
          if (modelName.toLowerCase().includes("demand")) {
            modelNames.add(modelName);
          }
        });
        return modelNames;
      }, new Set());

      const currentPeriod = getCurrentPeriod();

      const modelAccuracy = [...models].reduce((accuracies, model) => {
        const modelAccuracyList = Object.entries(data.Dpr).reduce((arr, [period, dprData]) => {
          if (
            parseInt(period, 10) <= parseInt(currentPeriod, 10) &&
            dprData.hasOwnProperty(DEMAND) && !isEmpty(data.DemandForecast[period]) &&
            data.DemandForecast[period].hasOwnProperty(model)
          ) {
            return [
              ...arr,
              100-Math.abs((dprData[DEMAND]-data.DemandForecast[period][model])/dprData[DEMAND]*100),
            ];
          }
          return arr;
        }, []);
        return isEmpty(modelAccuracyList) ? accuracies : {
          ...accuracies,
          [model]: roundTo2dp(
            modelAccuracyList.reduce((prev, curr) => prev + curr, 0) / modelAccuracyList.length
          ),
        }
      }, {});
      const highestAccuracyModelName = Object.keys(modelAccuracy).reduce((highestAccuracyModel, currentModel) => {
        if (highestAccuracyModel) {
          return modelAccuracy[highestAccuracyModel] > modelAccuracy[currentModel] ?
          highestAccuracyModel : currentModel;
        }
        return currentModel;
      }, null)
      return highestAccuracyModelName ?
        [highestAccuracyModelName, modelAccuracy[highestAccuracyModelName]] : null;
    }
    return null;
  }
  
  const getSdcDemandForecastByPeriod = (period, model) => (
    (
      !isEmpty(data.DemandForecast) && !isEmpty(data.DemandForecast[period]) &&
      data.DemandForecast[period][model]
    ) ? Math.round(data.DemandForecast[period][model]) : null
  );
  // Data processing END

  // Graph helper functions START
  const CustomTooltip = ({ active, payload, label }) => (
    active && payload && payload.length ?
      <Container key={`tooltip-${label}`} style={{backgroundColor: "var(--sp-light-blue)"}}>
        <p className="text-center"><u>{
          `Period ${label} (${periodToTime(parseInt(label, 10))} - ${periodToTime(parseInt(label, 10)+1)})`
        }</u></p>
        {
          payload
            .filter(entry => !(
              getCurrentPeriod() === entry.payload[PERIOD] &&
              !FORECAST_GRAPH_PARAMS.map(params => params[0]).find((name) => name == entry.name)
            )).map(entry => (
              <p className="text-center" style={{color: entry.color}}>
                {`${entry.name}: ` + (isPriceField(entry.name) ? `$${entry.value}` : `${entry.value} ${DEMAND_UNIT}`)}
              </p>
            ))
        }
        {
          payload[0].payload && getDemandDeltaFromPayload(payload[0].payload) ?
            <p className="text-center">
              {`ΔDemand (EMC-SDC): ${getDemandDeltaFromPayload(payload[0].payload)} ${DEMAND_UNIT}`}
            </p> : null
        }
        {
          payload[0].payload && getUsepDeltaFromPayload(payload[0].payload) ?
            <p className="text-center">
              {`ΔUSEP (EMC-SDC): ${getUsepDeltaFromPayload(payload[0].payload)}`}
            </p> : null
        }
      </Container> : null
  );

  const getForecastGraphYDomain = (data, emcActualVar, sdcActualVar) => {
    const getMax = (a, b) => a > b ? a : b;
    const getMin = (a, b) => a < b ? a : b;
    const getValue = (a, b, valFunc) => {
      if (typeof a == "string") {
        a = parseFloat(a);
      }
      if (typeof b == "string") {
        b = parseFloat(b);
      }
      if (a && b) {
        return valFunc(a, b);
      } else if (a && !b) {
        return a;
      } else if (!a && b) {
        return b;
      }
      return null;
    };
    const getLimit = (data, limitFunc, emcActualVar, sdcActualVar) => {
      const getForecastValue = (data, varName, isActualVar=true) => {
        const varNames = FORECAST_GRAPH_PARAMS.find(group => group[isActualVar ? 0 : 1] == varName);
        if (varNames) {
          return getValue(data[varNames[0]], data[varNames[1]], limitFunc);
        }
      }
      return data.reduce((prev, curr) => {
        const currLimit = getValue(
          getForecastValue(curr, emcActualVar),
          getForecastValue(curr, sdcActualVar),
          limitFunc
        );
        if (prev === null) { return currLimit; }
        return getValue(currLimit, prev, limitFunc);
      }, null);
    }
    const floor = getLimit(data, getMin, emcActualVar, sdcActualVar);
    const ceil = getLimit(data, getMax, emcActualVar, sdcActualVar);
    return [
      floor ? Math.floor(floor / 500) * 500 : 'auto',
      ceil ? Math.ceil(ceil / 500) * 500 : 'auto',
    ];
  }

  const getYAxisTickCount = (field) => 
    isEmpty(yDomains[field]) ? 'auto' : (Math.floor(yDomains[field][1]-yDomains[field][0])/500)+1;

  const getUsepDeltaFromPayload = (payload) => {
    const EMC_USEP = payload.hasOwnProperty(USEP) ? USEP : EMC_FORECASTED_USEP;
    const SDC_USEP = payload.hasOwnProperty(SDC_ACTUAL_USEP) ? SDC_ACTUAL_USEP : SDC_FORECASTED_USEP;  
    const delta = getDelta(payload[EMC_USEP], payload[SDC_USEP], 2);
    return delta ? `${delta[0]}$${delta.slice(1)}` : null;
  }
  const getDemandDeltaFromPayload = (payload) => {
    const EMC_DEMAND = payload.hasOwnProperty(EMC_ACTUAL_DEMAND) ? EMC_ACTUAL_DEMAND : EMC_FORECASTED_DEMAND;
    const SDC_DEMAND = payload.hasOwnProperty(SDC_ACTUAL_DEMAND) ? SDC_ACTUAL_DEMAND : SDC_FORECASTED_DEMAND;  
    return getDelta(payload[EMC_DEMAND], payload[SDC_DEMAND]);
  }
  const getDelta = (emc, sdc, round=0) => {
    const delta = emc != null && sdc != null && Math.abs(emc-sdc) > 0 ? (emc-sdc).toFixed(round) : null;
    return delta ? `${delta > 0 ? "+" : ""}${delta}`: null;
  }

  const isPriceField = (field) => field.match(/usep/gi);

  const CustomLegend = (props) => {
    const { payload, iconSize } = props;
    return (
      <Container fluid className="p-0 d-flex flex-row justify-content-center align-items-center">
        {
          FORECAST_GRAPH_PARAMS.map((keys, idx) => (
            <Container key={`legend-group-${idx}`} className="p-0 d-flex flex-column justify-content-center align-items-start">
              {
                keys.map((key, keyIdx) => {
                  const iconColour = payload.find(content => content.dataKey == key).color;
                  const iconStyles = {
                    stroke: iconColour ? iconColour : LINE_GRAPH_PALETTE[idx%LINE_GRAPH_PALETTE.length],
                    strokeWidth: 2,
                  }
                  if (keyIdx % 2) {
                    iconStyles.strokeDasharray = "3 1";
                  }
                  return (
                    <Container 
                      fluid
                      className="p-0 d-flex flex-row justify-content-start align-items-center column-gap-1"
                      key={`legend-${idx}-${keyIdx}`}
                    >
                      <svg width={iconSize} height={iconSize} viewBox="0 0 10 2">
                        <line x1="0" y1="1" x2="10" y2="1" style={iconStyles}/>
                      </svg>
                      <p>{key}</p>
                    </Container>
                  );
                })
              }
            </Container>
          ))
        }
      </Container>
    );
  };
  // Graph helper functions END

  if (loaded) {
    return (
      <div className="section-container overflow-hidden">
        <Container
          fluid className="p-2 position-relative d-flex flex-row align-items-center justify-content-center flex-grow-1"
          style={{minHeight: "50%"}}
        >
          {
            isEmpty(forecastData) ? getMissingDataMessage(forecastData) :
            <>
              <h5 className="w-100 mt-1 top-0 start-0 text-center position-absolute">SDC Demand & USEP Forecasts</h5>
              {/* FOR FUTURE REFERENCE: Model accuracy */}
              {/* {
                isEmpty(modelInfo) ? null :
                  <p className="w-75 top-0 end-0 text-end position-absolute me-2">
                    <i>Model Accuracy: {modelInfo[1]}%</i>
                  </p>
              } */}
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  width={750}
                  height={250}
                  data={forecastData}
                  margin={{ top: 30, right: 25, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={PERIOD}
                    type="number"
                    interval={0}
                    tickCount={48}
                    domain={[1,48]}
                    tick={{fontSize: 12}}
                    label={{ value: 'Period', position: 'insideBottomRight', offset: -5 }}
                  />
                  <YAxis
                    type="number"
                    domain={yDomains.demand}
                    interval={0}
                    tickCount={getYAxisTickCount("demand")}
                    label={{ value: `Demand (${DEMAND_UNIT})`, position: 'insideTopRight', offset: -25 }}
                    yAxisId="demand"
                  />
                  <YAxis
                    type="number"
                    domain={yDomains.usep}
                    interval={0}
                    tickCount={yDomains.usep[1] > 500 ? getYAxisTickCount("usep") : 6}
                    label={{ value: `USEP ($)`, position: 'insideTopLeft', offset: -25 }}
                    yAxisId="usep"
                    orientation="right"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="plainline" align="right" content={<CustomLegend />}/>
                  {
                    FORECAST_GRAPH_PARAMS.map((keys, idx) =>
                      <>
                        <Line
                          type="monotone"
                          dataKey={keys[0]}
                          key={`line-${keys[0]}`}
                          stroke={LINE_GRAPH_PALETTE[idx%LINE_GRAPH_PALETTE.length]}
                          dot={false}
                          yAxisId={isPriceField(keys[0]) ? "usep" : "demand"}
                        />
                        <Line
                          type="monotone"
                          dataKey={keys[1]}
                          key={`line-${keys[1]}`}
                          stroke={LINE_GRAPH_PALETTE[idx%LINE_GRAPH_PALETTE.length]}
                          dot={false}
                          strokeDasharray="3"
                          yAxisId={isPriceField(keys[1]) ? "usep" : "demand"}
                        />
                      </>
                    )
                  }
                  <ReferenceLine
                    x={timeToPeriod(getCurrentTime())}
                    stroke={SP_DARK_BLUE}
                    strokeDasharray="5 5"
                    strokeWidth={2.5}
                    isFront={false}
                    yAxisId="demand"
                  />
                </LineChart>
              </ResponsiveContainer>
              <div
                className="vr position-absolute start-100"
                style={{height: "calc(100% + 10px)", top: "-10px"}}
              ></div>
            </>
          }
        </Container>
        <Container
          fluid className="p-0 flex-grow-0"
          style={{height: "fit-content", maxHeight: "50%"}}
        >
          <table id="participation-table">
            <thead
              style={!isEmpty(participationData) && participationData.length > 6 ? {paddingRight: "8px"} : {}}
            >
              <tr style={{minHeight: "fit-content"}}>
                <th className="border-end">{PERIOD}</th>
                <th
                  className="border-start border-end"
                  style={{minWidth: "calc(10ch + 30px)"}}
                >
                  {TIME}
                </th>
                <th className="border-start border-end">SDC Demand Forecast</th>
                <th className="border-start border-end">{SOLAR_LOSS}</th>
                <th className="border-start border-end">{SDC_USEP}</th>
                <th className="border-start">Participate?</th>
                <th className="border-start">Curtailed Load</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{minHeight: "fit-content"}}>
                <td className="fw-bold border-end">{PERIOD}</td>
                <th
                  className="fw-bold border-start border-end"
                  style={{minWidth: "calc(10ch + 30px)"}}
                >
                  {TIME}
                </th>
                <td className="fw-bold border-start border-end">SDC Demand Forecast</td>
                <td className="fw-bold border-start border-end">{SOLAR_LOSS}</td>
                <td className="fw-bold border-start border-end">{SDC_USEP}</td>
                <td className="fw-bold border-start border-end">Participate?</td>
                <td className="fw-bold border-start border-end">Curtailed Load</td>
              </tr>
              {
                isEmpty(participationData) ?
                  <td className="position-relative" style={{height: "25vh"}} colSpan={7}>
                    {getMissingDataMessage(participationData)}
                  </td>
                  :
                  participationData.map((row, key) => {
                    return (
                      <tr key={`participation-${key}`} className={row[PERIOD] === getCurrentPeriod() ? "current-period-row" : ""}>
                        <td>{row[PERIOD]}</td>
                        <td>{row[TIME]}</td>
                        <td>{row[SDC_FORECASTED_DEMAND] ? `${row[SDC_FORECASTED_DEMAND]} ${DEMAND_UNIT}` : "-"}</td>
                        <td>{row[SOLAR_LOSS] ? row[SOLAR_LOSS] : "-"}</td>
                        <td>{row[SDC_USEP] ? `$${row[SDC_USEP]}` : "-"}</td>
                        <td>{row[PARTICIPATE]}</td>
                        <td>{row[CURTAILED_LOAD] ? `${row[CURTAILED_LOAD]} ${DEMAND_UNIT}` : "-"}</td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
        </Container>
      </div>
    );
  }
  return <Spinner animation="border" variant="primary" />;
};

export default MarketAnalysis;
